<!--

Shows our site's logo with a hyperlink back to /
If a compact logo is provided, it replaces the full logo on mobile view.
For PAWs partner members the PAWs logo is shown alongside ours, divided by a "|" vertical rule (or a "+" on mobile).

-->
<template>
	<a
		class="mia-Logo"
		:class="{ 'mia-Logo--maintenance': maintenance, 'mia-Logo--paws': showPawsLogo }"
		href="/"
		:title="$tr('Login0')"
	>
		<template v-if="effectiveCompactLogo">
			<responsive-image :from="effectiveCompactLogo" class="mia-LogoImage--compact" />
			<responsive-image :from="logo" class="mia-LogoImage--full" />
		</template>
		<responsive-image v-else-if="logo" :from="logo" />
		<template v-if="showPawsLogo">
			<responsive-image :from="plusIcon" aria-hidden="true" class="mia-LogoDividerPlus" />
			<span aria-hidden="true" class="mia-LogoDividerRule" />
			<responsive-image :from="pawsLogo" class="mia-PawsLogo" />
		</template>
	</a>
</template>
<script lang="ts">
import { Component, Prop, Vue, toNative } from "vue-facing-decorator";
import ResponsiveImage, { type Image } from "../../Control/Vue/ResponsiveImage";
import NavbarLogo from "@theme/logos/navbar_logo.svg?inline";
import DeveloperLogo from "@theme/logos/navbar_dev_logo.svg";
import MaintenanceLogo from "@theme/logos/maintenance_logo.svg";
import PawsLogo from "@theme/logos/paws_logo.svg?inline";
import PlusIcon from "@theme/icons/plus.svg?inline";
import MiniLogo from "@theme/logos/mini_logo.png";

@Component
({
	components: { ResponsiveImage },
})
export class Logo extends Vue {
	@Prop({ required: true, type: Boolean }) development!: boolean;
	@Prop({ required: false, type: Boolean, default: false }) maintenance!: boolean;
	@Prop({ required: false, default: null }) compactLogo!: Image | null;
	@Prop({ required: false, type: Boolean, default: false }) showPawsLogo!: boolean;

	readonly pawsLogo = PawsLogo;
	readonly plusIcon = PlusIcon;

	/**
	 * A PAWs header carries two logos, so mobile falls back to the compact site logo
	 * to keep both logos fitting even when no compact logo was passed in.
	 */
	get effectiveCompactLogo(): Image | null {
		if (this.compactLogo) {
			return this.compactLogo;
		}

		return this.showPawsLogo ? MiniLogo : null;
	}

	get logo(): Image {
		if (this.development) {
			return DeveloperLogo;
		}

		if (this.maintenance) {
			return MaintenanceLogo;
		}

		return NavbarLogo;
	}
}

export default toNative(Logo);
</script>
<style lang="scss">
$mia-logo-max-width: 260px;
$mia-paws-logo-gap: 6px;
$mia-paws-logo-gap-md: 20px;
$mia-paws-logo-height-md: 28px;

.mia-Logo {
	.mia-img {
		height: theme.$mia-logo-height;
		// Eliminate vertical space around the image in the containing <a> element
		vertical-align: top;
		max-width: $mia-logo-max-width;

		svg {
			// Override responsive-image width
			max-width: $mia-logo-max-width;
		}

		@media(max-width: theme.$grid-float-breakpoint-max) {
			height: theme.$mia-logo-height-sm;
		}
	}

	.mia-help-page .navbar-brand & .mia-img,
	.mia-landing-page .navbar-brand & .mia-img {
		height: 100%;
	}

	.mia-LogoImage--full {
		display: none;
	}

	.mia-LogoImage--compact {
		max-width: calc($mia-logo-max-width / 2);
	}

	@media(min-width: theme.$screen-sm-min) {
		.mia-LogoImage--compact {
			display: none;
		}

		.mia-LogoImage--full {
			display: block;
		}
	}
}

.mia-Logo--paws {
	display: flex;
	align-items: center;
	gap: $mia-paws-logo-gap;

	.mia-img {
		height: theme.$mia-icon-size-large;
	}

	.mia-PawsLogo.mia-img {
		height: theme.$mia-icon-size;

		svg {
			height: 100%;
			width: auto;
		}
	}

	.mia-LogoDividerPlus.mia-img {
		height: theme.$mia-icon-size-xs;
		width: theme.$mia-icon-size-xs;
		color: theme.$mia-color-black;

		svg {
			height: 100%;
			width: 100%;
		}
	}

	.mia-LogoDividerRule {
		display: none;
	}

	@media(min-width: theme.$screen-sm-min) {
		gap: theme.$mia-gap-size-large;

		.mia-LogoDividerPlus.mia-img {
			display: none;
		}

		.mia-LogoDividerRule {
			display: block;
			width: theme.$mia-border-width-panel;
			height: theme.$mia-icon-size;
			background-color: theme.$mia-color-black;
			border-radius: theme.$mia-border-radius-pill;
		}
	}

	@media(min-width: theme.$screen-md-min) {
		gap: $mia-paws-logo-gap-md;

		.mia-img {
			height: theme.$mia-logo-height-sm;
		}

		.mia-PawsLogo.mia-img,
		.mia-LogoDividerRule {
			height: $mia-paws-logo-height-md;
		}
	}
}
</style>
